import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  ReactFlow,
  Background,
  MiniMap,
  applyNodeChanges,
} from "@xyflow/react";
import { Info, X } from "lucide-react";
import PropTypes from "prop-types";
import DeviceNode from "./DeviceNode";
import { applyDagreLayout } from "../../utils/layoutUtils";
import "../../assets/styles/colors.css";

// Constants
/** ReactFlow-valid edge types — anything outside this list gets replaced. */
const VALID_EDGE_TYPES = new Set([
  "default",
  "straight",
  "step",
  "smoothstep",
  "bezier",
]);

// View modes cycle: icon -> compact -> full -> icon ...
const VIEW_MODES = ["icon", "compact", "full"];
const VIEW_MODE_LABELS = {
  icon: "Showing icons only — click for main fields",
  compact: "Showing main fields — click for all fields",
  full: "Showing all fields — click for icons only",
};

/**
 * Normalize a raw node coming from any API shape into a ReactFlow-ready node.
 * Handles both nested-data shape (topology page) and flat shape (boxes page).
 */
function normalizeNode(n, idx) {
  if (!n || n.id == null) return null;

  const nodeData = n.data && typeof n.data === "object" ? { ...n.data } : {};

  const nodeType = nodeData.nodeType || n.nodeType || n.type || "node";

  const label =
    nodeData.label || n.label || `${nodeType}: ${String(n.id ?? idx)}`;

  return {
    ...n,
    id: String(n.id ?? idx),
    type: "device",
    // Temporary grid position — dagre will always override these
    position: {
      x: (idx % 4) * 220,
      y: Math.floor(idx / 4) * 160,
    },
    data: {
      ...nodeData,
      nodeType,
      label,
    },
  };
}

/**
 * Normalize a raw edge coming from any API shape into a ReactFlow-ready edge.
 * Handles both {source/target} and {from/to} field names.
 * Forces a valid ReactFlow edge type — replaces semantic types like "ethernet".
 */
function normalizeEdge(e, idx, edgeColor) {
  if (!e) return null;

  const source = String(e.source ?? e.from ?? "");
  const target = String(e.target ?? e.to ?? "");
  if (!source || !target) return null;

  const hasLabel = !!e.label;

  return {
    ...e,
    id: e.id || `e-${source}-${target}-${idx}`,
    source,
    target,
    // Always force a valid ReactFlow edge type.
    // Semantic API types like "ethernet" are unknown to ReactFlow
    // and cause fallback rendering with messy curved lines.
    type: VALID_EDGE_TYPES.has(e.type) ? e.type : "smoothstep",
    style: {
      ...(e.style || {}),
      stroke: edgeColor,
      strokeWidth: 2,
    },
    ...(hasLabel && {
      labelStyle: {
        fill: edgeColor,
        fontWeight: 900,
        fontSize: 14,
        cursor: "pointer",
      },
      labelBgStyle: {
        fill: edgeColor,
        fillOpacity: 1,
        stroke: edgeColor,
        strokeWidth: 2,
      },
      labelBgPadding: [5, 5],
      labelBgBorderRadius: 50,
      labelShowBg: true,
    }),
  };
}

/**
 * Detect dark mode from the document.
 */
function getIsDarkMode() {
  if (typeof window === "undefined") return false;
  return (
    document.documentElement.classList.contains("dark") ||
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

/** Sidebar icon button — matches TopologyCanvas styling. */
function SideBtn({ onClick, title, active, children }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-200
        ${
          active
            ? "bg-(--color-primary)/20 text-(--color-primary)"
            : "text-(--color-primary) hover:bg-(--color-primary)/15 active:bg-(--color-primary)/25"
        }`}
    >
      {children}
    </button>
  );
}

SideBtn.propTypes = {
  onClick: PropTypes.func.isRequired,
  title: PropTypes.string,
  active: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

/** View-mode icon — reflects the current view (icon / compact / full). */
function ViewModeIcon({ viewMode }) {
  if (viewMode === "icon") {
    // icon-only state: a simple device glyph
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="4" width="18" height="12" rx="2" />
        <line x1="8" y1="20" x2="16" y2="20" />
        <line x1="12" y1="16" x2="12" y2="20" />
      </svg>
    );
  }
  if (viewMode === "compact") {
    // compact state: a couple of list lines (main fields)
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="4" y1="7" x2="20" y2="7" />
        <line x1="4" y1="12" x2="14" y2="12" />
      </svg>
    );
  }
  // full state: full list (all fields)
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="10" x2="20" y2="10" />
      <line x1="4" y1="14" x2="20" y2="14" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}

ViewModeIcon.propTypes = { viewMode: PropTypes.string };

/** Zoom-in icon. */
function ZoomInIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
      <line x1="11" y1="8" x2="11" y2="14" />
      <line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  );
}

/** Zoom-out icon. */
function ZoomOutIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
      <line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  );
}

/** Fit-view icon. */
function FitViewIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function TopologyPreviewModal({
  show,
  onClose,
  title,
  info,
  nodes: inputNodes = [],
  edges: inputEdges = [],
  showInfo = true,
}) {
  const { t } = useTranslation();
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  // Three-state view: "icon" (icons only), "compact" (main fields), "full" (all fields)
  const [viewMode, setViewMode] = useState("compact");
  const [hoveredEdge, setHoveredEdge] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const reactFlowInstance = useRef(null);

  const cycleViewMode = useCallback(() => {
    setViewMode((prev) => {
      const idx = VIEW_MODES.indexOf(prev);
      return VIEW_MODES[(idx + 1) % VIEW_MODES.length];
    });
  }, []);

  const details = {
    aiTitle: info?.aiTitle ?? null,
    aiDescription: info?.aiDescription ?? null,
    nodeCount: info?.nodeCount ?? inputNodes.length,
    edgeCount: info?.edgeCount ?? inputEdges.length,
  };

  // Patch nodeTypes so DeviceNode receives viewMode
  const patchedNodeTypes = useMemo(
    () => ({
      device: (props) => <DeviceNode {...props} viewMode={viewMode} />,
    }),
    [viewMode],
  );

  // Reset panel state when modal closes
  useEffect(() => {
    if (!show) {
      setIsDetailsOpen(false);
      setHoveredEdge(null);
    }
  }, [show]);

  // Normalize + layout whenever the modal opens or input data changes
  useEffect(() => {
    if (!show) return;

    const isDark = getIsDarkMode();
    const edgeColor = isDark ? "var(--color-primary)" : "#16a34a";

    // 1. Normalize nodes
    let nodeList = Array.isArray(inputNodes)
      ? inputNodes.map(normalizeNode).filter(Boolean)
      : [];

    // 2. Normalize edges
    const edgeList = Array.isArray(inputEdges)
      ? inputEdges
          .map((e, idx) => normalizeEdge(e, idx, edgeColor))
          .filter(Boolean)
      : [];

    // 3. Always apply dagre auto-layout.
    //    This is a read-only preview — clean layout is always preferred
    //    over whatever positions (good or bad) the API may have stored.
    if (nodeList.length > 0) {
      try {
        nodeList = applyDagreLayout(nodeList, edgeList, {
          direction: "TB",
        });
      } catch (err) {
        console.warn(
          "TopologyPreviewModal: dagre layout failed, using grid fallback:",
          err,
        );
      }
    }

    setNodes(nodeList);
    setEdges(edgeList);
  }, [show, inputNodes, inputEdges]);

  // Keyboard: Escape closes details panel first, then the modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!show || e.key !== "Escape") return;
      if (isDetailsOpen) {
        setIsDetailsOpen(false);
        return;
      }
      onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, onClose, isDetailsOpen]);

  const onNodesChange = useMemo(
    () => (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  );

  const onInit = useMemo(
    () => (instance) => {
      reactFlowInstance.current = instance;
      setTimeout(() => instance.fitView({ padding: 0.4, duration: 300 }), 100);
    },
    [],
  );

  const handleZoomIn = useCallback(() => {
    reactFlowInstance.current?.zoomIn({ duration: 200 });
  }, []);

  const handleZoomOut = useCallback(() => {
    reactFlowInstance.current?.zoomOut({ duration: 200 });
  }, []);

  const handleFitView = useCallback(() => {
    reactFlowInstance.current?.fitView({ duration: 300, padding: 0.1 });
  }, []);

  if (!show) return null;

  const isDarkMode = getIsDarkMode();

  const modalContent = (
    <div
      className="fixed inset-0 z-1000 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex flex-col w-[90vw] max-w-4xl h-[80vh] rounded-sm border border-(--color-border) bg-(--color-bg-card) shadow-[0_0_20px_var(--color-shadow-primary)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between border-b border-(--color-border) px-6 py-4 shrink-0">
          <h3 className="text-lg font-semibold text-(--color-text-white)">
            {title ||
              t("Topology preview", { defaultValue: "Topology preview" })}
          </h3>
          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center text-(--color-text-muted) transition-colors hover:border-(--color-primary)/40 hover:text-(--color-primary)"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── Canvas area ── */}
        <div className="flex-1 min-h-0 p-4 relative">
          {/* Left sidebar — filter + zoom controls */}
          <div className="absolute left-7 top-1/2 -translate-y-1/2 z-20 flex flex-col items-center gap-1.5 bg-(--color-bg-card) border border-(--color-primary)/25 rounded-md px-1 py-2">
            <SideBtn
              onClick={cycleViewMode}
              title={VIEW_MODE_LABELS[viewMode]}
              active={viewMode !== "icon"}
            >
              <ViewModeIcon viewMode={viewMode} />
            </SideBtn>

            <div className="w-5 h-px bg-(--color-primary)/20 my-0.5" />

            <SideBtn onClick={handleZoomIn} title="Zoom in">
              <ZoomInIcon />
            </SideBtn>

            <SideBtn onClick={handleZoomOut} title="Zoom out">
              <ZoomOutIcon />
            </SideBtn>

            <SideBtn onClick={handleFitView} title="Fit to view">
              <FitViewIcon />
            </SideBtn>
          </div>

          {/* Info button — top right */}
          {showInfo && (
            <div className="absolute top-7 right-7 z-20 group">
              <button
                type="button"
                className="flex h-9 w-9 items-center justify-center rounded-sm border border-(--color-border) bg-(--color-bg) text-(--color-text-muted) shadow-sm transition-colors hover:border-(--color-primary)/50 hover:text-(--color-primary)"
                aria-label={t("Topology details", {
                  defaultValue: "Topology details",
                })}
                aria-expanded={isDetailsOpen}
                onClick={() => setIsDetailsOpen((prev) => !prev)}
              >
                <Info className="h-4 w-4" />
              </button>

              <div
                className={`absolute right-0 z-30 mt-2 w-[calc(100vw-3rem)] max-w-88 rounded-sm border border-(--color-border) bg-(--color-bg) p-3 text-xs text-(--color-text) shadow-lg transition-all duration-150 wrap-break-words md:w-88 ${
                  isDetailsOpen
                    ? "pointer-events-auto opacity-100 visible translate-y-0"
                    : "pointer-events-none opacity-0 invisible translate-y-1 md:group-hover:pointer-events-auto md:group-hover:opacity-100 md:group-hover:visible md:group-hover:translate-y-0 md:group-focus-within:pointer-events-auto md:group-focus-within:opacity-100 md:group-focus-within:visible md:group-focus-within:translate-y-0"
                }`}
              >
                <div className="text-[11px] font-semibold text-(--color-text-muted) uppercase tracking-wide">
                  {t("Saved topology details", {
                    defaultValue: "Saved topology details",
                  })}
                </div>
                <div className="mt-2 space-y-2 leading-relaxed">
                  <p>
                    <span className="font-semibold text-(--color-text-muted)">
                      {t("AI title", { defaultValue: "AI title" })}:
                    </span>{" "}
                    {details.aiTitle ||
                      t("Not available", { defaultValue: "Not available" })}
                  </p>
                  <p>
                    <span className="font-semibold text-(--color-text-muted)">
                      {t("AI description", { defaultValue: "AI description" })}:
                    </span>{" "}
                    {details.aiDescription ||
                      t("Not available", { defaultValue: "Not available" })}
                  </p>
                  <p>
                    <span className="font-semibold text-(--color-text-muted)">
                      {t("Nodes", { defaultValue: "Nodes" })}:
                    </span>{" "}
                    {details.nodeCount}
                    {" | "}
                    <span className="font-semibold text-(--color-text-muted)">
                      {t("Edges", { defaultValue: "Edges" })}:
                    </span>{" "}
                    {details.edgeCount}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ReactFlow canvas */}
          <div className="relative w-full h-full rounded-sm overflow-hidden border border-(--color-border) bg-(--color-bg)">
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={patchedNodeTypes}
              onNodesChange={onNodesChange}
              onInit={onInit}
              onPaneClick={() => setIsDetailsOpen(false)}
              nodesDraggable
              nodesConnectable={false}
              elementsSelectable={false}
              panOnDrag
              zoomOnScroll
              fitView
              minZoom={0.05}
              onEdgeMouseEnter={(event, edge) => {
                if (edge?.data && Object.keys(edge.data).length > 0) {
                  setHoveredEdge(edge);
                  setTooltipPos({ x: event.clientX, y: event.clientY });
                }
              }}
              onEdgeMouseMove={(event) => {
                setTooltipPos({ x: event.clientX, y: event.clientY });
              }}
              onEdgeMouseLeave={() => setHoveredEdge(null)}
              proOptions={{ hideAttribution: true }}
              defaultEdgeOptions={{
                type: "smoothstep",
                style: {
                  stroke: isDarkMode ? "var(--color-primary)" : "#16a34a",
                  strokeWidth: 2,
                },
              }}
            >
              <Background gap={16} size={1} color="var(--color-primary-50)" />
              <MiniMap
                pannable
                zoomable
                style={{
                  width: 120,
                  height: 80,
                  backgroundColor: isDarkMode
                    ? "rgb(15 23 42 / 0.95)"
                    : "rgb(255 255 255 / 0.95)",
                  border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
                  borderRadius: "8px",
                  boxShadow: isDarkMode
                    ? "0 4px 6px rgb(0 0 0 / 0.3)"
                    : "0 1px 3px rgb(0 0 0 / 0.1)",
                }}
                nodeColor={() => "var(--color-primary)"}
                maskColor={
                  isDarkMode ? "rgba(0, 0, 0, 0.15)" : "rgba(0, 0, 0, 0.1)"
                }
              />
            </ReactFlow>
          </div>
        </div>

        {/* ── Edge hover tooltip ── */}
        {hoveredEdge && (
          <div
            className="fixed z-1100 pointer-events-none rounded-sm border border-(--color-border) bg-(--color-bg-card) shadow-[0_0_20px_var(--color-shadow-primary)] px-3 py-2 text-xs min-w-45 max-w-xs"
            style={{ left: tooltipPos.x + 12, top: tooltipPos.y + 12 }}
          >
            <div className="text-[10px] font-semibold text-(--color-text-muted) uppercase tracking-wide mb-1.5">
              {hoveredEdge.source} → {hoveredEdge.target}
            </div>
            <div className="space-y-1">
              {Object.entries(hoveredEdge.data || {}).map(([key, value]) => (
                <div key={key} className="flex gap-2 justify-between">
                  <span className="text-(--color-text-muted)">{key}</span>
                  <span className="font-mono text-(--color-text-white) truncate">
                    {typeof value === "object"
                      ? JSON.stringify(value)
                      : String(value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

TopologyPreviewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  info: PropTypes.shape({
    aiTitle: PropTypes.string,
    aiDescription: PropTypes.string,
    nodeCount: PropTypes.number,
    edgeCount: PropTypes.number,
  }),
  nodes: PropTypes.arrayOf(PropTypes.object),
  edges: PropTypes.arrayOf(PropTypes.object),
  showInfo: PropTypes.bool,
};

export default TopologyPreviewModal;